import { Button, Typography } from '@material-tailwind/react';
import { FreeTrialContextItem } from '../../constants/types/free-trial/type';
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePlayerToken } from '../../pages/contentsView/hooks/usePlayerToken';
import {
  ContentType,
  isPlayerHost,
  PlayerCloseEvent,
  PlayerMessageEvent,
} from '../../pages/contentsView/helpers';
import { useAppDispatch } from '../../store/hook';
import { reportFreeTrialStepProgress } from '../../api/utils/free-trial/api';
import { openAlert } from '../../store/features/modalSlice';
import { getErrorMessages } from '../../api/utils/util-func';
import FreeTrialAttentionEffect from './FreeTrialAttentionEffect';

interface FreeTrialReviewStepProps {
  freeTrialContext: FreeTrialContextItem;
  onComplete: () => void;
}

interface ReviewFinishEvent {
  type: 'PLAYER_TRIAL_FINISH';
  todoId: number;
}

const FreeTrialReviewStep = ({
  freeTrialContext,
  onComplete,
}: FreeTrialReviewStepProps) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const contentsData = useMemo(
    () => ({
      contentsUrl: freeTrialContext.review.link,
      contentsType: 'Jebspl' as ContentType,
    }),
    [freeTrialContext.review.link],
  );

  const { postTokenToPlayer, postTokenToPlayerRef } = usePlayerToken(
    contentsData,
    iframeRef,
  );

  const [isReviewCompleted, setIsReviewCompleted] = useState(false);

  useEffect(() => {
    const handleMessage = (
      event: MessageEvent<
        PlayerMessageEvent | PlayerCloseEvent | ReviewFinishEvent
      >,
    ) => {
      const data = event.data;

      if (
        data?.type !== 'PLAYER_READY' &&
        data?.type !== 'PLAYER_CLOSE' &&
        data?.type !== 'PLAYER_TRIAL_FINISH'
      ) {
        return;
      }

      const isFromPlayer =
        isPlayerHost(event.origin) &&
        event.source === iframeRef.current?.contentWindow;

      if (!isFromPlayer) return;

      if (data.type === 'PLAYER_READY') {
        postTokenToPlayerRef.current();
        return;
      }

      if (data.type === 'PLAYER_CLOSE') {
        return;
      }

      if (data.type === 'PLAYER_TRIAL_FINISH') {
        setIsReviewCompleted(true);
        return;
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [postTokenToPlayerRef]);

  const dispatch = useAppDispatch();

  const linkToken = sessionStorage.getItem('freeTrialLinkToken');

  useEffect(() => {
    if (!linkToken) return;

    const reportReviewProgress = async () => {
      try {
        await reportFreeTrialStepProgress({
          linkToken,
          step: 'review',
        });
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
        console.error('AI 인증제 진행 상태 저장 실패', error);
      }
    };

    void reportReviewProgress();
  }, [linkToken]);

  const handleCompleteReview = () => {
    onComplete();
  };

  return (
    <section className="mt-5 p-0 md:rounded-2xl md:bg-white md:px-6 md:py-7 md:shadow-[0_6px_20px_rgba(40,25,100,0.05)]">
      {/* 제목 */}
      <div>
        <Typography className="text-[11px] font-extrabold text-[#6737F4] md:text-[12px]">
          STEP 2 · 아이 혼자 · 젭스온
        </Typography>

        <Typography className="mt-1 text-[22px] font-black leading-[1.3] text-gray-900 md:text-[26px]">
          오늘 배운 문장을 혼자 복습해요
        </Typography>
      </div>

      {/* 온라인 복습 영역 */}
      <div className="mt-3 overflow-hidden rounded-2xl border border-[#DED8F2] bg-[#F8F5FF] md:mt-6">
        <iframe
          ref={iframeRef}
          src={freeTrialContext.review.link + '?trial=true'}
          title="젭스온 온라인 복습"
          className="h-[calc(100dvh-120px)] min-h-[840px] w-full border-0 md:h-[830px] md:min-h-0"
          allow="microphone; autoplay"
          onLoad={postTokenToPlayer}
        />
      </div>

      {/* 하단 액션 */}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Typography className="text-[11px] font-medium text-gray-700 md:text-[12px]">
          복습을 마치면 다음 단계인 AI 인증제가 열려요.
        </Typography>

        <Button
          ripple={false}
          onClick={handleCompleteReview}
          disabled={!isReviewCompleted}
          className="relative h-11 w-full shrink-0 rounded-xl bg-[#6737F4] px-5 py-0 text-[13px] font-bold normal-case text-white shadow-none transition-all hover:bg-[#5D2EE7] hover:shadow-none focus:shadow-none sm:w-auto"
        >
          {isReviewCompleted && <FreeTrialAttentionEffect />}

          <span className="relative z-10">복습 완료!</span>
        </Button>
      </div>
    </section>
  );
};

export default FreeTrialReviewStep;
